/**
 * Nodos analíticos N01..N45 — modo "Red nacional CALE".
 *
 * ⚠️ NOTA PROMINENTE [SIMULADO] / [DISEÑO]:
 * Un NODO ANALÍTICO ≠ UNA SEDE FÍSICA. Los nodos son una abstracción de
 * zonificación para el análisis educativo de cobertura (demanda, distancia,
 * capacidad). NO representan sedes CALE existentes, anunciadas ni
 * habilitadas. Ningún nodo afirma presencia física del esquema.
 *
 * Cada nodo se ubica en el centroide del principal municipio de su zona.
 * La cobertura se expresa por códigos DIVIPOLA de municipios del dataset
 * de muestra (municipios.sample.ts); cuando llegue la base real, la
 * zonificación se recalcula sobre los 1.122 municipios.
 */
export const NOTA_NODO_ANALITICO =
  "Nodo analítico ≠ sede física: abstracción de zonificación [SIMULADO] para análisis educativo de cobertura. No representa sede CALE existente, anunciada ni habilitada.";

export interface NodoAnalitico {
  /** Identificador N01..N45. */
  id: string;
  /** Nombre regional descriptivo. */
  nombre: string;
  /** Códigos DIVIPOLA de los municipios cubiertos por el nodo. */
  municipiosCubiertos: string[];
  /** Código DIVIPOLA del municipio principal (origen del centroide). */
  municipioPrincipal: string;
  /** Centroide = coordenadas del municipio principal. */
  lat: number;
  lon: number;
  estadoProbatorio: "SIMULADO";
}

type NodoSeed = [id: string, nombre: string, principal: string, lat: number, lon: number, cubiertos: string[]];

const NODO_SEEDS: readonly NodoSeed[] = [
  // Región Andina (21)
  ["N01", "Valle de Aburrá Norte", "05001", 6.244, -75.581, ["05001", "05088"]],
  ["N02", "Valle de Aburrá Sur", "05266", 6.17, -75.583, ["05266"]],
  ["N03", "Oriente Antioqueño", "05615", 6.155, -75.374, ["05615"]],
  ["N04", "Tunja y Centro Boyacá", "15001", 5.545, -73.367, ["15001"]],
  ["N05", "Tundama", "15238", 5.824, -73.033, ["15238"]],
  ["N06", "Manizales y Sur Caldense", "17001", 5.07, -75.52, ["17001"]],
  ["N07", "Magdalena Centro Caldense", "17380", 5.453, -74.663, ["17380"]],
  ["N08", "Bogotá y Sabana Norte", "11001", 4.711, -74.072, ["11001", "25899"]],
  ["N09", "Sabana de Occidente Sur", "25754", 4.579, -74.217, ["25754"]],
  ["N10", "Sabana de Occidente", "25269", 4.813, -74.354, ["25269"]],
  ["N11", "Alto Magdalena Cundinamarqués", "25307", 4.304, -74.8, ["25307"]],
  ["N12", "Neiva y Centro Huila", "41001", 2.927, -75.288, ["41001"]],
  ["N13", "Sur del Huila", "41551", 1.899, -76.042, ["41551"]],
  ["N14", "Cúcuta y Área Metropolitana", "54001", 7.894, -72.504, ["54001"]],
  ["N15", "Provincia de Ocaña", "54498", 8.233, -73.35, ["54498"]],
  ["N16", "Quindío Central", "63001", 4.534, -75.681, ["63001"]],
  ["N17", "Pereira y Dosquebradas", "66001", 4.814, -75.694, ["66001", "66170"]],
  ["N18", "Bucaramanga y Área Metropolitana", "68001", 7.119, -73.12, ["68001", "68276"]],
  ["N19", "Magdalena Medio Santandereano", "68081", 7.065, -73.855, ["68081"]],
  ["N20", "Ibagué y Sur Tolima", "73001", 4.438, -75.2, ["73001", "73268"]],
  ["N21", "Pasto y Frontera Sur", "52001", 1.208, -77.277, ["52001", "52356"]],
  // Región Caribe (10)
  ["N22", "Barranquilla y Área Metropolitana", "08001", 10.969, -74.8, ["08001", "08758"]],
  ["N23", "Cartagena y Bahía", "13001", 10.4, -75.5, ["13001"]],
  ["N24", "Depresión Momposina", "13430", 9.242, -74.755, ["13430"]],
  ["N25", "Valledupar y Valle del Cesar", "20001", 10.474, -73.243, ["20001"]],
  ["N26", "Montería y Sinú Medio", "23001", 8.748, -75.881, ["23001"]],
  ["N27", "Bajo Sinú", "23417", 9.237, -75.815, ["23417"]],
  ["N28", "Riohacha y Guajira Central", "44001", 11.544, -72.906, ["44001", "44430"]],
  ["N29", "Santa Marta y Ciénaga Grande", "47001", 11.24, -74.2, ["47001", "47189"]],
  ["N30", "Sincelejo y Sabana Sucreña", "70001", 9.295, -75.396, ["70001"]],
  ["N31", "Archipiélago de San Andrés", "88001", 12.583, -81.7, ["88001"]],
  // Región Pacífica (9)
  ["N32", "Quibdó y Atrato Medio", "27001", 5.692, -76.658, ["27001"]],
  ["N33", "Popayán y Centro Cauca", "19001", 2.444, -76.614, ["19001"]],
  ["N34", "Norte del Cauca", "19698", 3.0, -76.483, ["19698"]],
  ["N35", "Cali y Área Metropolitana", "76001", 3.437, -76.522, ["76001"]],
  ["N36", "Palmira y Valle Central", "76520", 3.539, -76.303, ["76520"]],
  ["N37", "Buenaventura y Litoral", "76109", 3.88, -77.031, ["76109"]],
  ["N38", "Urabá Antioqueño", "05045", 7.883, -76.633, ["05045"]],
  ["N39", "Centro del Valle", "76834", 4.083, -76.2, ["76834"]],
  ["N40", "Tumaco y Costa Pacífica Nariñense", "52835", 1.806, -78.765, ["52835"]],
  // Región Orinoquía (3)
  ["N41", "Villavicencio y Piedemonte", "50001", 4.142, -73.627, ["50001", "50006"]],
  ["N42", "Arauca y Frontera Norte", "81001", 7.084, -70.759, ["81001"]],
  ["N43", "Yopal, Aguazul y Vichada", "85001", 5.349, -72.396, ["85001", "85010", "99001"]],
  // Región Amazonía (2)
  ["N44", "Florencia, Guaviare y Putumayo", "18001", 1.614, -75.606, ["18001", "95001", "86001"]],
  ["N45", "Trapecio Amazónico, Guainía y Vaupés", "91001", -4.215, -69.94, ["91001", "94001", "97001"]],
];

export const NODOS_ANALITICOS: readonly NodoAnalitico[] = NODO_SEEDS.map(
  ([id, nombre, principal, lat, lon, cubiertos]) => ({
    id,
    nombre,
    municipiosCubiertos: cubiertos,
    municipioPrincipal: principal,
    lat,
    lon,
    estadoProbatorio: "SIMULADO",
  }),
);
